/* eslint-disable max-len */
/* global G */
import { asyncPipeSpread, asyncPipeSpreadIf, curry, deleteKey, getFirstItem, setKey } from 'lib/util'
import { getStateTerm, hasState } from 'app/_shared/events/address/index'
import init from 'app/_shared/events/address/partials/init'
import displayStateRow from 'app/_shared/events/address/partials/displayRow'
import { disable, enable } from 'lib/sequence/component/state/disabled'

/**
 * Returns whether the state field should be displayed for `country`.
 *
 * @param {string} country the selected country
 * @returns {boolean}
 */
const shouldDisplayState = country => !!(country && hasState(country))

/**
 * Controls the label of the state field
 *
 * @param {boolean} undo whether to set or reset the label
 *
 * @returns {function(Object, Gaia.AppModule.Spec, ...*): [Object,Gaia.AppModule.Spec, ...*]}
 */
const displayStateLabel = undo => (data, module, ...args) => {
  const { value, primary } = data

  if (undo) {
    deleteKey('label', primary[G.STATE])
    deleteKey('disableTranslation', primary[G.STATE])
    return [data, module, ...args]
  }

  const stateTerm = getStateTerm(value)
  const label = module[G.ADAPTER][G.INTL]._t(
    `label.${stateTerm}`,
    {
      ns: 'common',
      _key: `label.${stateTerm}`,
      defaultValue: stateTerm,
    },
  )

  setKey(label, 'label', primary[G.STATE])
  setKey(true, 'disableTranslation', primary[G.STATE])

  return [data, module, ...args]
}

/**
 * Enables/disables the state field validator based on business requirements.
 *
 * @param {boolean} shouldDisable   whether the validator should be disabled and following
 *                                  logic skipped
 * @returns {function(Object,Gaia.AppModule.Spec, ...*): [Object,Gaia.AppModule.Spec, ...*]}
 */
const toggleValidator = (shouldDisable) => (data, module, ...args) => {
  const { a: attributeInfo } = data
  const model = attributeInfo.attribute
    ? module[G.MODEL][G.CHILDREN][attributeInfo.attribute]
    : module[G.MODEL]

  const { [attributeInfo.parent]: parent, address } = model[G.CHILDREN]
  const { state } = parent
    ? parent[G.CHILDREN]?.address[G.CHILDREN]
    : address[G.CHILDREN]

  const stateValidator = getFirstItem(state[G.VALIDATOR])

  if (shouldDisable) {
    disable(stateValidator)
    return [data, module, ...args]
}

  const isCreating = !model[G.CACHE]
  const hadState = !!state[G.CACHE]

  // Logic for enabling the validator:
  // - Country should have a state (this is true if we land here in the first place) AND
  // - User is creating a new record OR user is editing one and state was set before
  isCreating || hadState
    ? enable(stateValidator)
    : disable(stateValidator)

  return [data, module, ...args]
}

/**
 * Manage State Event Handler
 *
 * Controls the visual appearance of the country and state fields in an address
 * form depending on the selected country.
 *
 * The `primary` field is the field that is actively mutated (shown/hidden) by this handler.
 * The `secondary` field is the field that is passively mutated (stretched/narrowed) by this
 * handler in depending on the state of the primary field.
 *
 * @param {string} primary              the primary field
 * @param {string} secondary            the secondary field
 * @param {ComponentInfo} componentInfo information about what component to use
 * @param {AttributeInfo} attributeInfo information about what model to use
 *
 * @returns {(function(...[*]): (*))|*}
 */
export default curry((primary, secondary, componentInfo, attributeInfo) => (module, component, event) => asyncPipeSpread(
  init(primary, secondary, componentInfo, attributeInfo),
  asyncPipeSpreadIf(({ value }) => shouldDisplayState(value))(
    displayStateLabel(false),
    displayStateRow(false),
    toggleValidator(false),
  ),
  asyncPipeSpreadIf(({ value }) => !shouldDisplayState(value))(
    displayStateLabel(true),
    displayStateRow(true),
    toggleValidator(true),
  ),
)(module, component, event))
